/* eslint-disable eqeqeq */
import { observable, computed, makeObservable } from "mobx";
import {
  getECG,
  fetchEcgEdit,
  getECGStatementLibrary,
  postEcgEdit,
} from "../../serviceCalls";

export default class ECGStore {
  loading;
  loadingECG;
  ECGList = [];
  statementLibrary;
  ECG;
  selectedECG;
  selectedCompareECG;
  selectedTool = "list";
  compareECG;
  animateOutCompare;
  selectedLead;
  selectedLeadIntoDone;
  scrubberWidth;
  selectedLeadIndex;
  compareMode;
  editMode;
  testsRelated;
  _caliperMode;
  marchOutMode;
  loadingStatementLibrary;
  checkingEdit;

  constructor() {
    makeObservable(this, {
      loading: observable,
      loadingECG: observable,
      ECGList: observable,
      statementLibrary: observable,
      ECG: observable,
      selectedECG: observable,
      selectedCompareECG: observable,
      selectedTool: observable,
      compareECG: observable,
      animateOutCompare: observable,
      selectedLead: observable,
      selectedLeadIntoDone: observable,
      scrubberWidth: observable,
      selectedLeadIndex: observable,
      compareMode: observable,
      editMode: observable,
      testsRelated: observable,
      _caliperMode: observable,
      marchOutMode: observable,
      loadingStatementLibrary: observable,
      checkingEdit: observable,
      caliperMode: computed,
      chartsStore: observable,
    });
  }

  fetchStatementLibrary(ecgId) {
    this.loadingStatementLibrary = true;
    fetchEcgEdit(ecgId)
      .then((res) => {
        if (res.allowEdit) {
          getECGStatementLibrary().then(
            (res) => (this.statementLibrary = res.statementLibrary),
          );
        }
      })
      .finally(() => (this.loadingStatementLibrary = false));
  }

  get singleLeadMode() {
    return this.selectedLeadIndex >= 0;
  }

  get caliperMode() {
    if (this.compareMode) return undefined;
    if (!this.singleLeadMode && this._caliperMode === "vertical")
      return undefined;
    return this._caliperMode;
  }
  set caliperMode(mode) {
    this._caliperMode = mode;
  }

  chartsStore = {
    width: 1200,
    get mVperSecond() {
      return 2.5;
    },
    quarterLeadWidth: 450,
    quarterLeadHeight: 180,
    startPosition: { x: 0, y: 0 },
    endPosition: { x: 1200 / 4, y: 0 },
    startRatio: 0,
    endRatio: 0.25,
    middlePosition: { x: 0, y: 0 },
    marchOutMode: true,
    selectedLead: null,
    ecg: null,
    yOffset: 0,
    get leads() {
      return this.selectedLead ? [this.selectedLead] : this.ecg.leads;
    },
    get scrubberWidth() {
      return this.endPosition.x - this.startPosition.x;
    },
    stripWidth: 1200,
    stripHeight: 150,
    gridHeight: 500,
    zoomAmount: 1,
    markers: observable([]),
    displayDetails: false,
    switchCaliperMode(mode) {
      if (!mode) {
        this.markers = observable([]);
        return;
      }
      let x = this.quarterLeadWidth / 2;
      let y = this.quarterLeadHeight / 2;
      this.markers = observable([
        { position: { x, y } },
        { position: { x, y } },
      ]);
      if (mode === "horizontal") {
        this.markers[0].position.x = (this.quarterLeadWidth * 1) / 3;
        this.markers[1].position.x = (this.quarterLeadWidth * 2) / 3;
      } else if (mode === "vertical") {
        this.markers[0].position.y = (this.quarterLeadHeight * 1) / 3;
        this.markers[1].position.y = (this.quarterLeadHeight * 2) / 3;
      }
      this.storeMarkers();
    },
    get timeRangeSeconds() {
      return this.ecg.leads[0].values.length / this.ecg.sampleBase;
    },
    get startTimeSeconds() {
      return (this.startPosition.x / this.stripWidth) * this.timeRangeSeconds;
    },
    get endTimeSeconds() {
      return (this.endPosition.x / this.stripWidth) * this.timeRangeSeconds;
    },
    positionToTime(x) {
      const secondsIntoQuarterLead =
        this.quarterLeadWidthSeconds * (x / this.quarterLeadWidth);
      return this.startTimeSeconds + secondsIntoQuarterLead;
    },
    get quarterLeadWidthSeconds() {
      return (this.scrubberWidth / this.stripWidth) * this.timeRangeSeconds;
    },
    pixelDistanceToVoltage(distance) {
      return distance / this.pixelsPerMv;
    },
    voltageToPosition(voltage) {
      return this.quarterLeadHeight / 2 - voltage * this.pixelsPerMv;
    },
    get pixelsPerSecond() {
      return this.quarterLeadWidth / this.quarterLeadWidthSeconds;
    },
    get pixelsPerMv() {
      return this.pixelsPerSecond / this.mVperSecond;
    },
    timeToPosition(seconds, isStrip) {
      if (isStrip) {
        return (this.stripWidth * seconds) / this.timeRangeSeconds;
      } else {
        return (
          ((seconds - this.startTimeSeconds) * this.quarterLeadWidth) /
          this.quarterLeadWidthSeconds
        );
      }
    },
    get scrubberSizeMin() {
      // Minimum of 3 minor grid squares AS1-5288
      const minorGridSquareSeconds = 0.04;
      return this.timeToPosition(minorGridSquareSeconds * 3, true);
    },
    storeMarker(m) {
      m.time = this.positionToTime(m.position.x);
      m.voltage = this.pixelDistanceToVoltage(
        this.quarterLeadHeight / 2 - m.position.y,
      );
    },
    storeMarkers() {
      this.markers.forEach((m) => {
        this.storeMarker(m);
      });
    },
    restoreMarkers() {
      this.markers.forEach((m) => {
        m.position = observable({
          x: this.timeToPosition(m.time),
          y: this.quarterLeadHeight / 2 - m.voltage * this.pixelsPerMv,
        });
      });
    },
    pan(xOffset) {
      const start = this.startPosition.x - xOffset;
      const end = this.endPosition.x - xOffset;
      if (start < 0 || end > this.stripWidth) {
        return;
      }
      this.startPosition = { x: start, y: 0 };
      this.endPosition = { x: end, y: 0 };
      this.middlePosition = { ...this.startPosition };
      this.restoreMarkers();
    },
  };

  get ecgLengthSeconds() {
    return this.ECG.lead[0].data.length / this.ECG.sampleRate;
  }

  get chartStore() {
    return this.chartsStore;
  }

  fetchECGComparison(query) {
    this.loadingECG = true;
    this.testsRelated = undefined;
    if (query.ecg2id === null) {
      query.ecg2id = this.selectedECG.ecgId;
    }
    return getECG(query)
      .then((res) => {
        this.testsRelated = res.testsRelated;
        this.ECG = res.ecgList.find((ecg) => ecg.ecgId == query.ecgId);
        this.compareECG = res.ecgList.find((ecg) => ecg.ecgId == query.ecg2id);
      })
      .finally(() => (this.loadingECG = false));
  }

  async fetchECG({ ecgId, asmpid }) {
    this.ECG = await getECG({
      asmpid: asmpid,
      ecgId: ecgId,
    }).catch(() => (this.loadingECG = false));

    if (this.ECG) {
      this.ECG.ecgId = this.ECG.ecgId.toString();
    }

    this.fetchStatementLibrary(this.ECG.ecgId);

    this.loadingECG = false;

    return this.ECG;
  }

  startEditMode() {
    if (!this.ECG) return;
    this.compareMode = false;
    this.checkingEdit = true;
    return fetchEcgEdit(this.ECG.ecgId)
      .catch(() => (this.editMode = false))
      .finally(() => (this.checkingEdit = false));
  }

  saveStatements(statements) {
    return postEcgEdit({
      ecgId: this.ECG.ecgId,
      statements,
    }).then((res) => {
      this.editMode = false;
      return res;
    });
  }
}
